/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {CACHE_SCOPE, DiscoveryCacheConfig, DiscoverySource, PATH_CACHE_METHOD} from '@exc/core/src/models/Discovery';
import {HISTORY, RUNTIME, SessionScope, Solution} from '@exc/core/src/models/Solution';

const cache: DiscoveryCacheConfig = {
  keepParams: [
    'company',
    'current_org'
  ],
  pathCacheMethod: PATH_CACHE_METHOD.CACHE_PARAMS,
  removeParams: [
    'jpj',
    'redirectedFromUnifiedShellHtml',
    'redirected',
    'ssSession',
    'unifiedShellImsRedirectForPhp'
  ],
  scope: CACHE_SCOPE.LOCAL,
  ttl: 31 * 86400 // One month
};

const payload = ['activeProductContext', 'imsOrg', 'path'];

const sessionDiscovery: DiscoverySource = {
  cache: {
    pathCacheMethod: PATH_CACHE_METHOD.CACHE_DEFAULT_PATH,
    removeParams: [
      'jpj',
      'ssSession'
    ],
    scope: CACHE_SCOPE.LOCAL,
    ttl: 31 * 86400
  },
  discovery: '{lastOrigin}/sc15/json/generatePhpSession.php',
  includesSession: true,
  payload: ['activeProductContext', 'company', 'tenant', 'imsOrg', 'imsProfile.reduced', 'path'],
  simpleRequest: true,
  withCredentials: true
};

const analytics: Solution = {
  appId: 'analytics',
  settingsAppId: 'analytics',
  distributionList: ['aaui-service-ops@adobe.com'],
  path: '/analytics',
  serviceCode: 'dma_analytics',
  analytics: {code: 'aa'},
  gainsight: {productKey: 'AP-WVMZ1DVKRDJD-2'},
  urlContext: {
    key: 'subOrg',
    config: {
      id: 'global_company_id',
      name: 'login_company',
      preferred: 'is_preferred',
      serviceCode: 'dma_analytics'
    }
  },
  sections: [
    {
      name: 'Analytics SPA',
      route: '/spa/',
      sessionIndicatorParameter: 'lazyLoadPhpSession',
      metricsAppId: 'analytics'
    },
    {
      name: 'Analytics PHP',
      route: '/p/',
      sessionIndicatorParameter: 'lazyLoadPhpSession',
      sessionParameter: 'ssSession',
      metricsAppId: 'analytics-php'
    },
    {
      name: 'Analytics PHP',
      route: '/sc15/',
      sessionIndicatorParameter: 'lazyLoadPhpSession',
      sessionParameter: 'ssSession',
      metricsAppId: 'analytics-php'
    }
  ],
  runtime: RUNTIME.CDN,
  sandbox: {
    hideInitialSpinner: true,
    history: {
      type: HISTORY.SERVER,
      config: {absolutePaths: true}
    },
    ims: {
      client_id: 'SiteCatalyst2',
      scope: 'openid,AdobeID,session,read_organizations,additional_info.job_function,additional_info.user_image_url,' +
        'additional_info.projectedProductContext,additional_info.roles'
    },
    sources: {
      dev: 'https://localhost.corp.adobe.com:8443/analytics.html',
      qa: {
        cache,
        discovery: 'https://app-discovery-qe.corp.ethos11-stage-or1.ethos.adobe.net/1.0/unifiedShell',
        payload,
        next: sessionDiscovery
      },
      stage: {
        cache,
        discovery: 'https://app-discovery-qe.corp.ethos11-stage-or1.ethos.adobe.net/1.0/unifiedShell',
        payload,
        next: sessionDiscovery
      },
      prod: {
        cache,
        discovery: 'https://analytics-discovery.adobe.net/1.0/unifiedShell',
        payload,
        next: sessionDiscovery
      }
    },
    pageTimeout: 300000
  },
  session: {
    defaultTTL: 7200, // 2 hours, can be extended by Analytics.
    getFromDiscovery: true,
    multiSessions: true,
    scope: SessionScope.CONTEXT
  },
  access: {code: ['dma_analytics']}
};

export default analytics;
